// Business template <-> Setup form conversions, plus the one-line summary
// shown wherever the template is displayed (Business config, the listing's
// "Turn on" screen). Pure functions only.
import { SlotCalendarSetupForm, SlotCalendarTemplate } from "../../interfaces/SlotCalendarObject";
import { addDays } from "./SlotCalendarTimeHelper";

export const TEMPLATE_HORIZON_OPTIONS: Array<{ days: number; label: string }> = [
  { days: 90, label: "3 months" },
  { days: 180, label: "6 months" },
  { days: 365, label: "12 months" },
  { days: 730, label: "24 months" },
];
export const DEFAULT_TEMPLATE_HORIZON_DAYS = 365;

const WEEKDAY_SHORT = ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function setupFormToTemplate(form: SlotCalendarSetupForm, timezone: string, horizonDays: number): SlotCalendarTemplate {
  return {
    version: 1,
    timezone,
    type: form.type,
    durationValue: form.durationValue,
    defaultCapacity: form.defaultCapacity,
    capacityLabelSingular: form.capacityLabelSingular,
    capacityLabelPlural: form.capacityLabelPlural,
    bookingCutoff: form.bookingCutoff,
    weekdays: form.type === "MONTHLY" ? null : form.weekdays,
    localStartTime: form.localStartTime,
    localEndTime: form.localEndTime,
    horizonDays,
  };
}

/** The listing's own setup form: the template, starting on `today`, with the
 *  listing's capacity (the one per-listing override). */
export function templateToSetupForm(template: SlotCalendarTemplate, today: string, capacity: number): SlotCalendarSetupForm {
  return {
    type: template.type,
    durationValue: template.durationValue,
    defaultCapacity: capacity,
    capacityLabelSingular: template.capacityLabelSingular,
    capacityLabelPlural: template.capacityLabelPlural,
    bookingCutoff: template.bookingCutoff,
    fromDate: today,
    toDateExclusive: addDays(today, template.horizonDays),
    weekdays: template.weekdays,
    localStartTime: template.localStartTime,
    localEndTime: template.localEndTime,
  };
}

/** Tolerant read of an untyped business-config value. */
export function readSlotCalendarTemplate(value: unknown): SlotCalendarTemplate | null {
  if (!value || typeof value !== "object") return null;
  const t = value as Partial<SlotCalendarTemplate>;
  return t.version === 1 && typeof t.type === "string" && typeof t.timezone === "string" ? (t as SlotCalendarTemplate) : null;
}

/** "30-min slots · Mon-Fri · 09:00-18:00 · 12 months" */
export function describeTemplate(t: SlotCalendarTemplate): string {
  const parts: string[] = [];
  if (t.type === "TIME_SLOTS") parts.push(`${t.durationValue}-min slots`);
  else if (t.type === "DAYS") parts.push("Daily");
  else if (t.type === "TRIPS") parts.push(`${t.durationValue}-night trips`);
  else parts.push("Monthly");

  if (t.type !== "MONTHLY") {
    parts.push(t.weekdays && t.weekdays.length > 0 ? t.weekdays.map((d) => WEEKDAY_SHORT[d]).join(", ") : "Every day");
  }
  if (t.type === "TIME_SLOTS" && t.localStartTime && t.localEndTime) {
    parts.push(`${t.localStartTime.slice(0, 5)}-${t.localEndTime.slice(0, 5)}`);
  } else if (t.type === "TRIPS" && t.localStartTime) {
    parts.push(`departs ${t.localStartTime.slice(0, 5)}`);
  }
  const horizon = TEMPLATE_HORIZON_OPTIONS.find((o) => o.days === t.horizonDays)?.label ?? `${t.horizonDays} days`;
  parts.push(horizon);
  return parts.join(" · ");
}
